import React, { useState, useEffect, useLayoutEffect } from "react";
import { useModelViewerReady } from "../helpers/load-model-viewer";
import useOutsideClick from "../helpers/outsideclick-helper";
import { createGestures } from "../helpers/model-viewer-gestures";
import ViewerButtons from "../helpers/viewer-buttons";
import { quietFocusRing } from "../helpers/quiet-focus-ring";
import { leaveNavKeysToApp } from "../helpers/viewer-keys";

const prefix = "/static/carousel";
const models = [
  "-xvfJwzmooUWtg",
  "6A1ezZxCCTw4ag",
  "FydGdmUZJ_LU6g",
  "nKzCBkjuUYxNSA",
  "RFXmQczriNhaGg",
];

const HeroScroller = () => {
  const [ct, setCt] = useState(0);
  const [src, setSrc] = useState(`${prefix}/${models[ct]}.glb`);
  const [idle, setIdle] = useState(true);
  const [iosSrc, setIosSrc] = useState(`${prefix}/${models[ct]}.usdz`);
  const mvRef = React.useRef();
  // The flip / rotate buttons the fork used to draw (BZL-212).
  const [gestures, setGestures] = useState(null);

  // Fetches the viewer element on demand; the tag below upgrades once it lands.
  useModelViewerReady();

  useLayoutEffect(() => {
    const { current } = mvRef;
    if (!current) return;
    quietFocusRing(current);
    const releaseNavKeys = leaveNavKeysToApp(current);
    const g = createGestures(current);
    setGestures(g);
    current.addEventListener("camera-change", (e) => {
      if (e.detail.source === "user-interaction") {
        setIdle(false);
      }
    });
    return () => {
      g.dispose();
      releaseNavKeys();
    };
  }, [mvRef]);

  // A different carousel model starts un-turned.
  useEffect(() => {
    gestures?.reset();
  }, [gestures, src]);

  useOutsideClick(mvRef, () => {
    setIdle(true);
  });

  useEffect(() => {
    const interval = idle
      ? setInterval(() => {
          // Advance to the NEXT model, then store that index. Both halves
          // were wrong: `ct < 5 ? ct + 1 : 0` let ct reach 5, and
          // models[5 % 5] is models[0], so the first model was shown again at
          // ct=5 and once more at ct=0 -- 24s on one model every cycle. And
          // setting src from the current ct before incrementing meant the
          // view always lagged the counter by one tick.
          const next = (ct + 1) % models.length;
          setSrc(`${prefix}/${models[next]}.glb`);
          setIosSrc(`${prefix}/${models[next]}.usdz`);
          setCt(next);
        }, 12000)
      : null;
    return () => clearInterval(interval);
  }, [ct, idle]);

  return (
    <model-viewer
      class="carousel"
      src={src}
      ios-src={iosSrc}
      alt="3D Model"
      shadow-intensity="0"
      stage-light-intensity="10"
      environment-intensity="2"
      environment-image="/static/white.png"
      camera-orbit="0deg 75deg 105%"
      exposure="1"
      max-field-of-view="85deg"
      auto-rotate
      camera-controls
      /* Off as in the box viewer: 4.x's prompt wiggles the camera under the
         flip/rotate buttons, and a tap re-targets the camera (BZL-212). */
      interaction-prompt="none"
      disable-tap
      disable-pan
      /* The fork's 45deg framing lens; see model-viewer-hooks.jsx. */
      field-of-view="45deg"
      ar
      magic-leap
      quick-look-browsers="safari chrome"
      ref={mvRef}
      onClick={() => setIdle(true)}
      style={{ "--progress-bar-color": "rgb(28, 182, 245, 0.3)" }}
    >
      <ViewerButtons gestures={gestures} />
    </model-viewer>
  );
};

export default HeroScroller;
